import { gql } from '@apollo/client';
import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import useAppSelector from '../app/redux/useAppSelector';
import { MenuItem } from '../components/Menu';
import type { VerifyMenuItems_PostFragment } from '../graphql/generated/graphqlTypes';
import historyIcon from '../icons/history.svg';
import verifiedIcon from '../icons/verified.svg';
import UIActions from '../ui/UIActions';
import CreateVerificationModal from './CreateVerificationModal';
import EditVerificationModal from './EditVerificationModal';
import getVerificationStatus, {
  VerificationStatus,
} from './getVerificationStatus';
import {
  isAllowedToVerify,
  PostPolicyAction,
  usePostPolicyCheck,
} from './postPolicyCheck';
import RequestVerificationModal from './RequestVerificationModal';
import VerifyPostModal from './VerifyPostModal';

const POST_FRAGMENT = gql`
  fragment VerifyMenuItems_post on Post {
    verification {
      id
    }
    archivedAt
    ...usePostPolicyCheck_post
    ...VerifyPostModal_post
    ...EditVerificationModal_post
    ...RequestVerificationModal_post
    ...getVerificationStatus_post
    ...isAllowedToVerify_post
  }
  ${usePostPolicyCheck.fragments.post}
  ${VerifyPostModal.fragments.post}
  ${EditVerificationModal.fragments.post}
  ${RequestVerificationModal.fragments.post}
  ${getVerificationStatus.fragments.post}
  ${isAllowedToVerify.fragments.post}
`;

export const useShouldRenderVerifyMenuItems = () => {
  const postPolicyCheck = usePostPolicyCheck();

  return useCallback(
    ({
      post,
      user,
    }: {
      post: VerifyMenuItems_PostFragment;
      user: { id: string };
    }) => {
      return (
        post.publishedAt &&
        !post.archivedAt &&
        (postPolicyCheck(PostPolicyAction.EditPost, post, user) ||
          isAllowedToVerify(post, user))
      );
    },
    [postPolicyCheck],
  );
};

interface VerifyMenuItemsProps {
  post: VerifyMenuItems_PostFragment;
}

const VerifyMenuItems = ({ post }: VerifyMenuItemsProps) => {
  const user = useAppSelector((state) => state.session.user);
  const dispatch = useDispatch();
  const postPolicyCheck = usePostPolicyCheck();

  if (post.verification) {
    return (
      <>
        {isAllowedToVerify(post, user) &&
          (getVerificationStatus(post) === VerificationStatus.Requested ? (
            <MenuItem
              icon={verifiedIcon}
              onClick={() =>
                dispatch(UIActions.openModal(VerifyPostModal, { post }))
              }
            >
              Verify
            </MenuItem>
          ) : (
            <MenuItem
              icon={historyIcon}
              onClick={() =>
                dispatch(UIActions.openModal(VerifyPostModal, { post }))
              }
            >
              Reverify
            </MenuItem>
          ))}
        {postPolicyCheck(PostPolicyAction.EditPost, post, user) && (
          <MenuItem
            icon={verifiedIcon}
            onClick={() =>
              dispatch(
                UIActions.openModal(EditVerificationModal, {
                  post,
                }),
              )
            }
          >
            Edit Verification
          </MenuItem>
        )}
      </>
    );
  }

  const isUserAllowedToVerifyPost = isAllowedToVerify(post, user);

  return postPolicyCheck(PostPolicyAction.EditPost, post, user) ||
    isUserAllowedToVerifyPost ? (
    <MenuItem
      icon={verifiedIcon}
      onClick={() =>
        dispatch(
          UIActions.openModal(
            isUserAllowedToVerifyPost
              ? CreateVerificationModal
              : RequestVerificationModal,
            {
              post,
            },
          ),
        )
      }
    >
      Verify
    </MenuItem>
  ) : null;
};

VerifyMenuItems.fragments = { post: POST_FRAGMENT };

export default VerifyMenuItems;
